/**
 * ILAVYA Theme JavaScript
 * Replaces React state, context, and router with vanilla JavaScript event listeners,
 * DOM updates, LocalStorage state, and the native Shopify Ajax Cart API.
 */

window.ILAVYA = window.ILAVYA || {};

document.addEventListener('DOMContentLoaded', () => {
  ILAVYA.initHeader();
  ILAVYA.initCart();
  ILAVYA.initWishlist();
  ILAVYA.initCompare();
  ILAVYA.initQuickView();
  ILAVYA.initSearch();
  ILAVYA.initAccordions();
  ILAVYA.initProductGallery();
  ILAVYA.initProductVariants();
  ILAVYA.initRitualFinder();
  ILAVYA.initIngredientIndex();
  ILAVYA.initFilters();
  ILAVYA.initToasts();
  ILAVYA.initReviewModal();
  ILAVYA.initScrollReveal();
});

// ── Toasts Manager ────────────────────────────────────────────────────────
ILAVYA.initToasts = function() {
  const container = document.createElement('div');
  container.className = 'toasts';
  container.id = 'theme-toasts';
  document.body.appendChild(container);

  ILAVYA.toast = function(message, actionLabel = '', actionCallback = null) {
    const toast = document.createElement('div');
    toast.className = 'toast';
    toast.innerHTML = `<span>${message}</span>`;
    
    if (actionLabel && actionCallback) {
      const btn = document.createElement('a');
      btn.href = '#';
      btn.textContent = actionLabel;
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        actionCallback();
        toast.remove();
      });
      toast.appendChild(btn);
    }
    
    container.appendChild(toast);
    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transform = 'translate(-50%, 12px)';
      setTimeout(() => toast.remove(), 320);
    }, 4000);
  };
};

// ── Header Scroll & Mega Menus ──────────────────────────────────────────────
ILAVYA.initHeader = function() {
  const header = document.querySelector('.header');
  if (!header) return;

  // Sticky header class toggle
  window.addEventListener('scroll', () => {
    if (window.scrollY > 4) {
      header.classList.add('is-stuck');
    } else {
      header.classList.remove('is-stuck');
    }
  }, { passive: true });

  // Mega menu toggle buttons
  const navlinks = header.querySelectorAll('.navlink');
  let openMenu = null;

  navlinks.forEach(btn => {
    if (btn.tagName === 'BUTTON') {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const menuType = btn.textContent.toLowerCase().includes('shop') ? 'shop' : 'learn';
        const targetMenu = header.querySelector(`.mega[data-menu="${menuType}"]`);
        
        if (openMenu === menuType) {
          btn.classList.remove('is-open');
          if (targetMenu) targetMenu.style.display = 'none';
          openMenu = null;
        } else {
          header.querySelectorAll('.navlink').forEach(nb => nb.classList.remove('is-open'));
          header.querySelectorAll('.mega').forEach(mm => mm.style.display = 'none');
          
          btn.classList.add('is-open');
          if (targetMenu) targetMenu.style.display = 'block';
          openMenu = menuType;
        }
      });
    }
  });

  // Click outside mega menu closes it
  document.addEventListener('click', () => {
    header.querySelectorAll('.navlink').forEach(btn => btn.classList.remove('is-open'));
    header.querySelectorAll('.mega').forEach(menu => menu.style.display = 'none');
    openMenu = null;
  });

  // Nested (3rd-level) dropdowns fly out to the right by default; flip to the
  // left when that would push them off the viewport edge.
  header.querySelectorAll('.dropdown-menu__item--has-sub').forEach(item => {
    const nested = item.querySelector(':scope > .dropdown-menu--nested');
    if (!nested) return;
    item.addEventListener('pointerenter', () => {
      nested.classList.remove('is-edge');
      const rect = nested.getBoundingClientRect();
      if (rect.right > window.innerWidth) {
        nested.classList.add('is-edge');
      }
    });
  });

  // Mobile Menu Panel Toggle
  const mobBtn = header.querySelector('.mobnav');
  if (mobBtn) {
    mobBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const currentOverlay = document.body.getAttribute('data-overlay');
      if (currentOverlay === 'menu') {
        ILAVYA.closeOverlay();
      } else {
        ILAVYA.openOverlay('menu');
      }
    });
  }
};

// ── Overlay Framework ───────────────────────────────────────────────────────
ILAVYA.openOverlay = function(type) {
  document.body.setAttribute('data-overlay', type);
  document.body.classList.add('is-locked');
  
  const headerIcon = document.querySelector('.mobnav svg');
  if (headerIcon && type === 'menu') {
    headerIcon.style.transform = 'rotate(90deg)';
  }
};

ILAVYA.closeOverlay = function() {
  document.body.removeAttribute('data-overlay');
  document.body.classList.remove('is-locked');
  const headerIcon = document.querySelector('.mobnav svg');
  if (headerIcon) {
    headerIcon.style.transform = 'none';
  }
};

// ── Shopify AJAX Cart Drawer ────────────────────────────────────────────────
ILAVYA.initCart = function() {
  ILAVYA.refreshCartDrawer();

  document.addEventListener('click', (e) => {
    if (e.target.classList.contains('scrim') || e.target.closest('.drawer__head .icon-btn')) {
      if (document.body.getAttribute('data-overlay')) {
        ILAVYA.closeOverlay();
      }
    }
  });

  const mobMenu = document.getElementById('mobile-menu');
  if (mobMenu) {
    mobMenu.addEventListener('click', (e) => {
      if (e.target.closest('a')) {
        ILAVYA.closeOverlay();
      }
    });
  }

  const bagBtns = document.querySelectorAll('[aria-label*="Bag"], .header__actions button[onClick*="cart"]');
  bagBtns.forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      ILAVYA.openOverlay('cart');
    });
  });

  // Keep the "bought together" item count and total accurate as boxes are toggled
  document.addEventListener('change', (e) => {
    if (!e.target.classList || !e.target.classList.contains('fbt-checkbox')) return;
    const form = e.target.closest('form');
    const totals = form && form.querySelector('#fbt-totals');
    if (!totals) return;
    let total = parseFloat(totals.getAttribute('data-main-price') || 0);
    let count = 1;
    form.querySelectorAll('.fbt-checkbox:checked').forEach(cb => {
      total += parseFloat(cb.getAttribute('data-price') || 0);
      count += 1;
    });
    const rupees = total > 10000 ? Math.round(total / 100) : Math.round(total);
    const totalEl = form.querySelector('#fbt-total-price-display');
    if (totalEl) totalEl.textContent = `₹${rupees.toLocaleString('en-IN')}`;
    const countEl = form.querySelector('#fbt-items-count');
    if (countEl) countEl.textContent = `${count} item${count !== 1 ? 's' : ''}`;
  });

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[action*="/cart/add"]');
    if (form) {
      e.preventDefault();
      const formData = new FormData(form);
      
      const items = [];
      const checkedFbt = form.querySelectorAll('.fbt-checkbox:checked');
      if (checkedFbt.length > 0) {
        checkedFbt.forEach(cb => {
          items.push({ id: cb.value, quantity: 1 });
        });
      }
      
      if (form.querySelector('[name="id"]')) {
        items.push({
          id: formData.get('id'),
          quantity: formData.get('quantity') || 1
        });
      }

      const body = JSON.stringify({ items });
      
      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body
      })
      .then(res => res.json())
      .then(data => {
        ILAVYA.refreshCartDrawer();
        ILAVYA.openOverlay('cart');
        ILAVYA.toast('Added to bag');
      })
      .catch(err => {
        console.error('Error adding to cart:', err);
        ILAVYA.toast('Could not add to bag');
      });
    }
  });
};

ILAVYA.quickAdd = function(variantId, quantity = 1) {
  if (!variantId) return;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';
  fetch(root + 'cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items: [{ id: Number(variantId), quantity: Number(quantity) }] })
  })
  .then(res => res.json())
  .then(data => {
    if (ILAVYA.refreshCartDrawer) ILAVYA.refreshCartDrawer();
    if (ILAVYA.openOverlay) ILAVYA.openOverlay('cart');
    if (ILAVYA.toast) ILAVYA.toast('Added to bag');
  })
  .catch(err => {
    console.error('Error adding to cart:', err);
    if (ILAVYA.toast) ILAVYA.toast('Could not add to bag');
  });
};

ILAVYA.addMultipleToCart = function(items) {
  if (!items || !items.length) return;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';
  const payload = items.map(item => ({
    id: Number(item.id || item.variantId),
    quantity: Number(item.quantity || 1)
  }));

  fetch(root + 'cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items: payload })
  })
  .then(res => res.json())
  .then(data => {
    if (ILAVYA.refreshCartDrawer) ILAVYA.refreshCartDrawer();
    if (ILAVYA.openOverlay) ILAVYA.openOverlay('cart');
    if (ILAVYA.toast) ILAVYA.toast('Added full routine to bag!');
  })
  .catch(err => {
    console.error('Error adding routine to cart:', err);
    if (ILAVYA.toast) ILAVYA.toast('Could not add routine to bag');
  });
};

ILAVYA.refreshCartDrawer = function() {
  fetch(window.Shopify.routes.root + 'cart.js')
    .then(res => res.json())
    .then(cart => {
      const counts = document.querySelectorAll('.icon-btn__count, .bag-count-badge');
      counts.forEach(badge => {
        if (badge.closest('[aria-label*="Wishlist"]')) return;
        if (cart.item_count > 0) {
          badge.textContent = cart.item_count;
          badge.style.display = 'grid';
        } else {
          badge.textContent = '';
          badge.style.display = 'none';
        }
      });

      const drawerBody = document.querySelector('#cart-drawer-body');
      const drawerFoot = document.querySelector('#cart-drawer-foot');
      
      if (!drawerBody || !drawerFoot) return;

      if (cart.item_count === 0) {
        drawerBody.innerHTML = `
          <div class="empty">
            <span class="empty__mark"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5-2 4-2 4 2 4 2"/></svg></span>
            <h3 class="display-3">Your bag is empty</h3>
            <p class="muted" style="margin-top:var(--s-3);">Your wellness blends will show up here as soon as you add them.</p>
            <button type="button" class="btn btn--primary" style="margin-top:var(--s-5);" onclick="ILAVYA.closeOverlay()">Continue browsing</button>
          </div>
        `;
        drawerFoot.style.display = 'none';
      } else {
        drawerFoot.style.display = 'block';
        
        let html = '';
        cart.items.forEach(item => {
          const variantText = item.variant_title ? `<span class="line__meta">${item.variant_title}</span>` : '';
          
          html += `
            <div class="line" data-id="${item.key}">
              <div class="line__media">
                <img src="${item.image || 'data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 4 5\'%3E%3C/svg%3E'}" alt="${item.product_title}" loading="lazy" width="68">
              </div>
              <div class="line__body">
                <div class="line__top">
                  <h4 class="line__name"><a href="${item.url}">${item.product_title}</a></h4>
                  <span class="pcard__price" style="font-size: 0.95rem;">₹${(item.final_line_price / 100).toFixed(0)}</span>
                </div>
                ${variantText}
                <div class="row row--between" style="margin-top:var(--s-3);">
                  <div class="qty">
                    <button type="button" aria-label="Decrease quantity" onclick="ILAVYA.changeQty('${item.key}', ${item.quantity - 1})">
                      <svg width="12" height="2" viewBox="0 0 12 2" fill="currentColor"><rect width="12" height="2" rx="1"/></svg>
                    </button>
                    <span class="qty__n">${item.quantity}</span>
                    <button type="button" aria-label="Increase quantity" onclick="ILAVYA.changeQty('${item.key}', ${item.quantity + 1})">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor"><path d="M5 1v10h2V1H5zm6 4H1v2h10V5z"/></svg>
                    </button>
                  </div>
                  <button type="button" class="btn btn--quiet btn--sm" style="color:var(--ink-4);" onclick="ILAVYA.changeQty('${item.key}', 0)">Remove</button>
                </div>
              </div>
            </div>
          `;
        });
        
        const limit = 999;
        const subtotal = cart.total_price / 100;
        const progress = Math.min((subtotal / limit) * 100, 100);
        const diff = limit - subtotal;
        
        let shipText = `You have earned <strong>Free Shipping!</strong>`;
        if (diff > 0) {
          shipText = `Spend <strong>₹${diff.toFixed(0)} more</strong> for free shipping`;
        }

        const shipProgressHtml = `
          <div class="ship" style="margin-bottom: var(--s-5);">
            <div class="small" style="display:flex; justify-content:space-between;">
              <span>${shipText}</span>
              <span class="muted">Limit: ₹${limit}</span>
            </div>
            <div class="ship__track">
              <div class="ship__fill" style="width: ${progress}%;"></div>
            </div>
          </div>
        `;
        
        drawerBody.innerHTML = shipProgressHtml + html + `<div id="cart-crosssell"></div>`;
        ILAVYA.renderCartCrossSell(cart);
        
        drawerFoot.innerHTML = `
          <div class="row row--between" style="margin-bottom:var(--s-4);">
            <span class="label">Total value</span>
            <strong class="display-3" style="font-size:1.25rem;">₹${subtotal.toFixed(0)}</strong>
          </div>
          <p class="small muted" style="margin-bottom:var(--s-4); line-height:1.45;">Shipping, discounts and taxes calculated at checkout. Delivered across India in 3–5 business days.</p>
          <a href="/checkout" class="btn btn--primary btn--block">Proceed to checkout</a>
        `;
      }
    });
};

/* ── Cart cross-sell ────────────────────────────────────────────────────────
   "Complete your ritual" — Shopify's own recommendations for the first item
   in the bag, so a suggestion is always related to what the customer chose
   rather than picked at random. Silent on any failure: the bag still works. */
ILAVYA.renderCartCrossSell = function(cart) {
  const mount = document.getElementById('cart-crosssell');
  if (!mount || !cart || !cart.items || !cart.items.length) return;

  const seed = cart.items[0].product_id;
  const inBag = cart.items.map(i => i.product_id);
  const root = window.Shopify.routes.root || '/';

  fetch(`${root}recommendations/products.json?product_id=${seed}&limit=6&intent=complementary`)
    .then(res => (res.ok ? res.json() : null))
    .then(data => {
      if (!data || !data.products) return;

      const picks = data.products
        .filter(p => inBag.indexOf(p.id) === -1 && p.available)
        .slice(0, 2);
      if (!picks.length) return;

      const rows = picks.map(p => {
        const variant = (p.variants || []).find(v => v.available) || p.variants[0];
        if (!variant) return '';
        const img = p.featured_image || (p.images && p.images[0]) || '';
        const price = Math.round(variant.price / 100);
        return `
          <div class="xsell__row">
            ${img ? `<img class="xsell__img" src="${img}" alt="" width="56" height="70" loading="lazy">` : ''}
            <div class="xsell__meta">
              <a href="${p.url}" class="xsell__name">${p.title}</a>
              <span class="xsell__price">₹${price}</span>
            </div>
            <button type="button" class="btn btn--ghost btn--sm" onclick="ILAVYA.quickAdd('${variant.id}')">Add</button>
          </div>`;
      }).join('');

      mount.innerHTML = `
        <div class="xsell">
          <span class="label">Complete your ritual</span>
          ${rows}
        </div>`;
    })
    .catch(() => { /* recommendations are a bonus, never a blocker */ });
};

ILAVYA.changeQty = function(key, qty) {
  fetch(window.Shopify.routes.root + 'cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: key, quantity: qty })
  })
  .then(res => res.json())
  .then(cart => {
    ILAVYA.refreshCartDrawer();
  })
  .catch(err => {
    console.error('Error changing qty:', err);
    ILAVYA.toast('Could not update quantity');
  });
};

// ── Wishlist Manager (LocalStorage) ──────────────────────────────────────────
ILAVYA.initWishlist = function() {
  const STORAGE_KEY = 'ilavya.wishlist';
  ILAVYA.getWishlist = () => JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
  ILAVYA.saveWishlist = (list) => localStorage.setItem(STORAGE_KEY, JSON.stringify(list));

  ILAVYA.updateWishlistCount = function() {
    const list = ILAVYA.getWishlist();
    const badges = document.querySelectorAll('.wishlist-count-badge, [aria-label*="Wishlist"] .icon-btn__count');
    badges.forEach(badge => {
      if (list.length > 0) {
        badge.textContent = list.length;
        badge.style.display = 'grid';
      } else {
        badge.textContent = '';
        badge.style.display = 'none';
      }
    });
  };

  ILAVYA.toggleWishlist = function(handle, elementBtn) {
    let list = ILAVYA.getWishlist();
    const index = list.indexOf(handle);
    let added = false;
    
    if (index > -1) {
      list.splice(index, 1);
    } else {
      list.push(handle);
      added = true;
    }
    
    ILAVYA.saveWishlist(list);
    ILAVYA.updateWishlistCount();
    
    document.querySelectorAll(`[data-wishlist-toggle="${handle}"]`).forEach(btn => {
      if (added) {
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');
        const icon = btn.querySelector('svg');
        if (icon) icon.style.fill = 'currentColor';
      } else {
        btn.classList.remove('is-active');
        btn.setAttribute('aria-pressed', 'false');
        const icon = btn.querySelector('svg');
        if (icon) icon.style.fill = 'none';
      }
    });

    ILAVYA.toast(added ? 'Saved to wishlist' : 'Removed from wishlist');
    
    if (document.querySelector('.wishlist-page-container')) {
      ILAVYA.renderWishlistPage();
    }
  };

  const syncBtnStates = () => {
    const list = ILAVYA.getWishlist();
    document.querySelectorAll('[data-wishlist-toggle]').forEach(btn => {
      const handle = btn.getAttribute('data-wishlist-toggle');
      if (list.includes(handle)) {
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');
        const icon = btn.querySelector('svg');
        if (icon) icon.style.fill = 'currentColor';
      }
    });
  };

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (btn) {
      e.preventDefault();
      e.stopPropagation();
      const handle = btn.getAttribute('data-wishlist-toggle');
      ILAVYA.toggleWishlist(handle, btn);
    }
  });

  syncBtnStates();
  ILAVYA.updateWishlistCount();

  if (document.querySelector('.wishlist-page-container')) {
    ILAVYA.renderWishlistPage();
  }
};

ILAVYA.renderWishlistPage = function() {
  const container = document.querySelector('.wishlist-page-container');
  const handles = ILAVYA.getWishlist();
  
  if (handles.length === 0) {
    container.innerHTML = `
      <div class="empty">
        <span class="empty__mark"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/></svg></span>
        <h3 class="display-3">Your wishlist is empty</h3>
        <p class="muted" style="margin-top:var(--s-3);">Your saved wellness blends will appear here as soon as you bookmark them.</p>
        <a href="/collections/all" class="btn btn--primary" style="margin-top:var(--s-5);">Explore all products</a>
      </div>
    `;
    return;
  }

  container.innerHTML = `<div class="wrap text-center" style="padding:var(--s-8);"><span class="label">Loading saved blends…</span></div>`;
  
  const promises = handles.map(handle => 
    fetch(`${window.Shopify.routes.root}products/${handle}.js`).then(res => {
      if (res.ok) return res.json();
      return null;
    })
  );

  Promise.all(promises).then(products => {
    const filtered = products.filter(Boolean);
    if (filtered.length === 0) {
      container.innerHTML = `<p class="muted">No products found.</p>`;
      return;
    }

    let gridHtml = `<div class="pgrid">`;
    filtered.forEach(p => {
      const img = p.images[0] ? `<img class="pack__img" src="${p.images[0]}" alt="${p.title}">` : `
        <div class="pack__sachet" role="img" aria-label="${p.title} sachet">
          <div>
            <div class="pack__sachet-kicker">CATALOGUE</div>
            <div class="pack__sachet-name">${p.title}</div>
          </div>
          <div class="pack__sachet-foot">ILAVYA · Premix</div>
        </div>
      `;
      
      const category = p.type || 'Wellness';
      
      gridHtml += `
        <article class="pcard" style="--accent: #b4542b; --accent-soft: #f6e3d8;">
          <div class="pcard__media">
            <button type="button" class="icon-btn pcard__wish is-active" data-wishlist-toggle="${p.handle}" aria-pressed="true" aria-label="Remove from wishlist">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1.5"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>
            </button>
            <a href="${p.url}">
              <div class="pack">${img}</div>
            </a>
            <div class="pcard__tray">
              <button type="button" class="btn btn--light btn--sm" onclick="ILAVYA.openQuickView('${p.handle}')">Quick view</button>
              <button type="button" class="btn btn--sm" onclick="ILAVYA.quickAdd('${p.variants[0].id}')">Add</button>
            </div>
          </div>
          <div class="pcard__body">
            <div class="pcard__kicker">
              <span class="label label--accent">${category}</span>
            </div>
            <h3 class="pcard__name">
              <a href="${p.url}" class="link">${p.title}</a>
            </h3>
            <p class="pcard__desc">${p.description.substring(0, 80)}…</p>
            <div class="pcard__foot">
              <span class="pcard__price">₹${(p.price / 100).toFixed(0)}</span>
            </div>
          </div>
        </article>
      `;
    });
    gridHtml += `</div>`;
    container.innerHTML = gridHtml;
  });
};

ILAVYA.quickAdd = function(variantId) {
  fetch(window.Shopify.routes.root + 'cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
  })
  .then(res => res.json())
  .then(() => {
    ILAVYA.refreshCartDrawer();
    ILAVYA.openOverlay('cart');
    ILAVYA.toast('Added to bag');
  });
};

// ── Compare Manager (LocalStorage) ──────────────────────────────────────────
ILAVYA.initCompare = function() {
  const STORAGE_KEY = 'ilavya.compare';
  const MAX_ITEMS = 3;

  ILAVYA.getCompareList = () => JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
  ILAVYA.saveCompareList = (list) => localStorage.setItem(STORAGE_KEY, JSON.stringify(list));

  ILAVYA.renderCompareBar = function() {
    const list = ILAVYA.getCompareList();
    let bar = document.querySelector('#compare-tray-bar');
    
    if (list.length === 0 || window.location.pathname.includes('/pages/compare')) {
      if (bar) bar.remove();
      return;
    }

    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'comparebar';
      bar.id = 'compare-tray-bar';
      document.body.appendChild(bar);
    }

    const promises = list.map(handle => 
      fetch(`${window.Shopify.routes.root}products/${handle}.js`).then(res => res.ok ? res.json() : null)
    );

    Promise.all(promises).then(products => {
      const filtered = products.filter(Boolean);
      let html = `<div class="comparebar__thumbs">`;
      
      filtered.forEach(p => {
        const imageSrc = p.images[0] || 'data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 1 1\'%3E%3C/svg%3E';
        html += `
          <div class="comparebar__thumb">
            <img src="${imageSrc}" alt="${p.title}" width="38" height="38" style="object-fit:cover;">
            <button type="button" aria-label="Remove ${p.title}" onclick="ILAVYA.toggleCompare('${p.handle}')">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
          </div>
        `;
      });

      html += `</div>`;
      
      html += `
        <span class="small" style="color:var(--ink-2); font-weight:500;">
          ${filtered.length} of ${MAX_ITEMS} blends selected
        </span>
        <div class="row">
          <a href="/pages/compare" class="btn btn--sm btn--primary">Compare now</a>
          <button type="button" class="btn btn--quiet btn--sm" onclick="ILAVYA.clearCompareList()">Clear</button>
        </div>
      `;

      bar.innerHTML = html;
    });
  };

  ILAVYA.toggleCompare = function(handle) {
    let list = ILAVYA.getCompareList();
    const index = list.indexOf(handle);
    let added = false;

    if (index > -1) {
      list.splice(index, 1);
    } else {
      if (list.length >= MAX_ITEMS) {
        ILAVYA.toast(`Compare holds ${MAX_ITEMS} blends — remove one first`);
        return;
      }
      list.push(handle);
      added = true;
    }

    ILAVYA.saveCompareList(list);
    ILAVYA.renderCompareBar();

    document.querySelectorAll(`[data-compare-toggle="${handle}"]`).forEach(btn => {
      // Icon only — the control is a fixed-size circle, so a text label spilled
      // outside the card. State rides on aria-pressed instead of visible words.
      if (added) {
        btn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6L9 17l-5-5"/></svg>`;
        btn.style.color = '';
        btn.setAttribute('aria-pressed', 'true');
      } else {
        btn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M16 4h6v6M8 20H2v-6M12 12m-8 0a8 8 0 1 0 16 0 8 8 0 1 0-16 0"/></svg>`;
        btn.style.color = '';
        btn.setAttribute('aria-pressed', 'false');
      }
    });

    if (document.querySelector('.compare-page-container')) {
      ILAVYA.renderComparePage();
    }
  };

  ILAVYA.clearCompareList = function() {
    ILAVYA.saveCompareList([]);
    ILAVYA.renderCompareBar();
    document.querySelectorAll('[data-compare-toggle]').forEach(btn => {
      btn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M16 4h6v6M8 20H2v-6M12 12m-8 0a8 8 0 1 0 16 0 8 8 0 1 0-16 0"/></svg>`;
      btn.style.color = '';
      btn.setAttribute('aria-pressed', 'false');
    });
    if (document.querySelector('.compare-page-container')) {
      ILAVYA.renderComparePage();
    }
  };

  const list = ILAVYA.getCompareList();
  document.querySelectorAll('[data-compare-toggle]').forEach(btn => {
    const handle = btn.getAttribute('data-compare-toggle');
    if (list.includes(handle)) {
      btn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6L9 17l-5-5"/></svg>`;
      btn.style.color = '';
      btn.setAttribute('aria-pressed', 'true');
    }
  });

  ILAVYA.renderCompareBar();

  if (document.querySelector('.compare-page-container')) {
    ILAVYA.renderComparePage();
  }
};

ILAVYA.renderComparePage = function() {
  const container = document.querySelector('.compare-page-container');
  const handles = ILAVYA.getCompareList();
  
  if (handles.length === 0) {
    container.innerHTML = `
      <div class="empty">
        <span class="empty__mark"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/></svg></span>
        <h3 class="display-3">No blends selected</h3>
        <p class="muted" style="margin-top:var(--s-3);">Select up to three products to compare benefits, ingredients and calories side-by-side.</p>
        <a href="/collections/all" class="btn btn--primary" style="margin-top:var(--s-5);">Explore catalog</a>
      </div>
    `;
    return;
  }

  container.innerHTML = `<div class="wrap text-center" style="padding:var(--s-8);"><span class="label">Loading comparison matrix…</span></div>`;

  const promises = handles.map(handle => 
    fetch(`${window.Shopify.routes.root}products/${handle}.js`).then(res => res.ok ? res.json() : null)
  );

  Promise.all(promises).then(products => {
    const filtered = products.filter(Boolean);
    
    let html = `
      <div class="ctable-wrap">
        <table class="ctable">
          <thead>
            <tr>
              <th></th>
    `;
    
    filtered.forEach(p => {
      const img = p.images[0] || 'data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 1 1\'%3E%3C/svg%3E';
      html += `
        <th>
          <div style="display:flex; flex-direction:column; gap:var(--s-2);">
            <img src="${img}" alt="${p.title}" style="width:72px; height:72px; object-fit:cover; border-radius:var(--r-sm);">
            <strong>${p.title}</strong>
            <span class="small muted">₹${(p.price / 100).toFixed(0)}</span>
            <button type="button" class="btn btn--sm btn--primary" onclick="ILAVYA.quickAdd('${p.variants[0].id}')">Add to bag</button>
            <button type="button" class="btn btn--quiet btn--sm" style="color:var(--err);" onclick="ILAVYA.toggleCompare('${p.handle}')">Remove</button>
          </div>
        </th>
      `;
    });

    html += `
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Description</td>
    `;
    filtered.forEach(p => {
      html += `<td>${p.description.substring(0, 160)}…</td>`;
    });
    
    html += `
            </tr>
            <tr>
              <td>Dietary Labels</td>
    `;
    filtered.forEach(p => {
      html += `<td>${p.type || 'Standard'}</td>`;
    });

    html += `
            </tr>
            <tr>
              <td>Preparation</td>
    `;
    filtered.forEach(p => {
      html += `<td>Mix 1 sachet in 200ml of hot or cold water. Stir 10 seconds.</td>`;
    });

    html += `
            </tr>
          </tbody>
        </table>
      </div>
    `;

    container.innerHTML = html;
  });
};

// ── Search Command Overlay ──────────────────────────────────────────────────
/* ── Ingredient-aware search ────────────────────────────────────────────────
   A search for a botanical should return more than products: the index entry
   for the ingredient itself, and anything the Journal has written about it.
   Both groups are additive — products still lead the results. */

ILAVYA.searchIngredients = function () {
  if (ILAVYA._ingredients) return ILAVYA._ingredients;
  const el = document.getElementById('ilavya-ingredients');
  let list = [];
  try {
    const raw = el ? JSON.parse(el.textContent) : '';
    list = String(raw).split(',').map(s => s.trim()).filter(Boolean);
  } catch (e) { list = []; }
  ILAVYA._ingredients = list;
  return list;
};

ILAVYA.searchExtraGroups = function (q) {
  const needle = String(q || '').toLowerCase();
  if (needle.length < 2) return '';
  const hits = ILAVYA.searchIngredients().filter(name => {
    const n = name.toLowerCase();
    return n.indexOf(needle) === 0 || needle.indexOf(n) === 0;
  }).slice(0, 3);
  if (!hits.length) return '';

  let html = `<div class="search__group">`;
  html += `<div class="search__grouphead"><span class="label">Ingredient</span></div>`;
  hits.forEach(name => {
    html += `
      <a href="/pages/ingredients" class="search__row search__row--ing">
        <span class="search__ingmark" aria-hidden="true"></span>
        <span>
          <strong>${name}</strong>
          <span class="small muted">What it is and what it does</span>
        </span>
      </a>`;
  });
  html += `</div>`;
  return html;
};

ILAVYA.searchJournal = function (q, mount) {
  if (!mount || String(q || '').length < 2) return;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  fetch(`${root}search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=article&resources[limit]=3`)
    .then(r => (r.ok ? r.json() : null))
    .then(data => {
      const arts = (data && data.resources && data.resources.results && data.resources.results.articles) || [];
      if (!arts.length) return;
      let html = `<div class="search__group">`;
      html += `<div class="search__grouphead"><span class="label">Journal</span></div>`;
      arts.forEach(a => {
        html += `
          <a href="${a.url}" class="search__row search__row--ing">
            <span class="search__ingmark" aria-hidden="true"></span>
            <span>
              <strong>${a.title}</strong>
              <span class="small muted">Journal</span>
            </span>
          </a>`;
      });
      html += `</div>`;
      mount.insertAdjacentHTML('beforeend', html);
    })
    .catch(() => { /* journal results are additive, never blocking */ });
};

ILAVYA.initSearch = function() {
  const searchBtns = document.querySelectorAll('[aria-label*="Search"], .icon-btn[aria-label="Search"]');
  let catalogCache = null;

  function loadCatalog() {
    if (catalogCache) return Promise.resolve(catalogCache);
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';
    return fetch(`${root}collections/all/products.json?limit=250`)
      .then(res => res.json())
      .then(data => {
        catalogCache = (data && data.products) ? data.products : [];
        return catalogCache;
      })
      .catch(err => {
        console.error('Error fetching catalog for search:', err);
        return [];
      });
  }

  // Pre-fetch catalog when search overlay triggers
  searchBtns.forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      ILAVYA.openOverlay('search');
      loadCatalog();
      setTimeout(() => {
        const inp = document.querySelector('#predictive-search-input');
        if (inp) inp.focus();
      }, 150);
    });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && document.body.getAttribute('data-overlay') === 'search') {
      ILAVYA.closeOverlay();
    }
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      const current = document.body.getAttribute('data-overlay');
      if (current === 'search') {
        ILAVYA.closeOverlay();
      } else {
        ILAVYA.openOverlay('search');
        loadCatalog();
        setTimeout(() => {
          const inp = document.querySelector('#predictive-search-input');
          if (inp) inp.focus();
        }, 150);
      }
    }
  });

  const searchInput = document.querySelector('#predictive-search-input');
  const resultsDiv = document.querySelector('#predictive-search-results');

  function renderSearchResults(products, q) {
    if (!resultsDiv) return;
    if (products.length === 0) {
      const extra = ILAVYA.searchExtraGroups(q);
      resultsDiv.innerHTML = extra
        ? extra
        : `<p class="muted" style="padding:var(--s-4); text-align:center;">Nothing matches "${q}" yet</p>`;
      ILAVYA.searchJournal(q, resultsDiv);
      return;
    }
    let html = ILAVYA.searchExtraGroups(q);
    html += `<div class="search__group">`;
    html += `<div class="search__grouphead"><span class="label">Products (${products.length})</span></div>`;
    products.forEach(p => {
      const img = p.image || (p.featured_image ? (p.featured_image.url || p.featured_image) : '');
      const priceFormatted = typeof p.price === 'number' ? `₹${p.price.toFixed(0)}` : (p.price || '');
      html += `
        <a href="${p.url}" class="search__row" style="display:flex; align-items:center; gap:var(--s-3); padding:var(--s-3); text-decoration:none; border-radius:var(--r-md);">
          <div class="search__thumb" style="width:44px; height:44px; flex-shrink:0;">
            <img src="${img}" alt="${p.title}" style="width:100%; height:100%; object-fit:contain;" onerror="this.style.display='none';">
          </div>
          <div>
            <strong style="color:var(--ink); font-size:var(--t-sm); display:block;">${p.title}</strong>
            <span class="small muted">${priceFormatted}</span>
          </div>
        </a>
      `;
    });
    html += `</div>`;
    resultsDiv.innerHTML = html;
    ILAVYA.searchJournal(q, resultsDiv);
  }

  if (searchInput && resultsDiv) {
    let debounceTimer = null;
    searchInput.addEventListener('input', () => {
      clearTimeout(debounceTimer);
      const q = searchInput.value.trim().toLowerCase();
      if (q.length < 2) {
        resultsDiv.innerHTML = '';
        return;
      }

      debounceTimer = setTimeout(async () => {
        const products = await loadCatalog();
        
        // Match query against title, tags (e.g. Serve: Cold), body_html, product_type, variants
        const matches = products.filter(p => {
          if (p.title && p.title.toLowerCase().includes(q)) return true;
          if (p.tags && Array.isArray(p.tags) && p.tags.some(tag => tag.toLowerCase().includes(q))) return true;
          if (p.body_html && p.body_html.toLowerCase().includes(q)) return true;
          if (p.product_type && p.product_type.toLowerCase().includes(q)) return true;
          if (p.variants && p.variants.some(v => v.title && v.title.toLowerCase().includes(q))) return true;
          return false;
        }).slice(0, 8);

        if (matches.length > 0) {
          renderSearchResults(matches.map(p => {
            const firstVariant = (p.variants && p.variants[0]) ? p.variants[0] : {};
            const firstImage = (p.images && p.images[0]) ? p.images[0].src : '';
            return {
              title: p.title,
              url: `/products/${p.handle}`,
              image: firstImage,
              price: firstVariant.price ? `₹${parseFloat(firstVariant.price).toFixed(0)}` : ''
            };
          }), q);
        } else {
          // Fallback to Shopify predictive suggest API with wildcard
          const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';
          fetch(`${root}search/suggest.json?q=${encodeURIComponent(q)}*&resources[type]=product&resources[limit]=6`)
            .then(res => res.json())
            .then(data => {
              const suggestProducts = (data && data.resources && data.resources.results && data.resources.results.products) || [];
              renderSearchResults(suggestProducts, q);
            })
            .catch(() => {
              renderSearchResults([], q);
            });
        }
      }, 150);
    });
  }
};

// ── Quick View Engine ───────────────────────────────────────────────────────
ILAVYA.initQuickView = function() {
  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'quick-view-modal';
  modal.style.display = 'none';
  document.body.appendChild(modal);

  ILAVYA.openQuickView = function(handle) {
    modal.innerHTML = `<div class="scrim" onclick="ILAVYA.closeQuickView()"></div>`;
    modal.style.display = 'grid';
    document.body.classList.add('is-locked');

    fetch(`${window.Shopify.routes.root}products/${handle}.js`)
      .then(res => res.json())
      .then(p => {
        const plain = (p.description || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
        const dot = plain.indexOf('. ');
        const desc = dot > 20 ? plain.slice(0, dot + 1) : plain.slice(0, 160);
        const img = p.images[0] || 'data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 4 5\'%3E%3C/svg%3E';
        
        let variantsHtml = '';
        p.variants.forEach((v, index) => {
          variantsHtml += `
            <button type="button" class="opt ${index === 0 ? 'is-on' : ''}" data-variant-id="${v.id}" data-variant-price="₹${Math.round(v.price/100).toLocaleString('en-IN')}" onclick="ILAVYA.selectQuickViewVariant(this)">
              <span class="opt__radio"></span>
              <span class="opt__main">
                <span class="opt__label">${v.title}</span>
              </span>
              <span class="opt__price">₹${Math.round(v.price/100).toLocaleString('en-IN')}</span>
            </button>
          `;
        });

        const panel = document.createElement('div');
        panel.className = 'modal__panel';
        panel.innerHTML = `
          <button type="button" class="icon-btn modal__close" onclick="ILAVYA.closeQuickView()">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
          <div class="qv-grid pdp" style="padding:var(--s-6); grid-template-columns: 1fr 1fr;">
            <div>
              <div class="pack" style="--accent: #b4542b; --accent-soft:#f6e3d8;">
                <img src="${img}" alt="${p.title}" style="width:100%; object-fit:contain;">
              </div>
            </div>
            <div>
              <span class="label label--accent">${p.type || 'Wellness'}</span>
              <h2 class="display-2 buybox__title">${p.title}</h2>
              <p class="muted" style="margin-top:var(--s-3);">${desc}</p>
              
              <div style="margin-top:var(--s-5);">
                <div class="label" style="margin-bottom:var(--s-3);">Select Format</div>
                ${variantsHtml}
              </div>

              <div class="buybox__price" style="margin-top:var(--s-5);">
                <strong id="qv-price-tag">₹${Math.round(p.price/100).toLocaleString('en-IN')}</strong>
              </div>

              <form action="/cart/add" method="post" id="qv-add-form">
                <input type="hidden" name="id" id="qv-selected-variant-id" value="${p.variants[0].id}">
                <input type="hidden" name="quantity" value="1">
                <button type="submit" class="btn btn--primary btn--block">Add to bag</button>
              </form>
            </div>
          </div>
        `;
        
        modal.appendChild(panel);

        const qvForm = panel.querySelector('#qv-add-form');
        qvForm.addEventListener('submit', (e) => {
          e.preventDefault();
          const selectedId = panel.querySelector('#qv-selected-variant-id').value;
          fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: selectedId, quantity: 1 }] })
          })
          .then(res => res.json())
          .then(() => {
            ILAVYA.closeQuickView();
            ILAVYA.refreshCartDrawer();
            ILAVYA.openOverlay('cart');
            ILAVYA.toast('Added to bag');
          });
        });
      });
  };

  ILAVYA.closeQuickView = function() {
    modal.style.display = 'none';
    modal.innerHTML = '';
    document.body.classList.remove('is-locked');
  };

  ILAVYA.selectQuickViewVariant = function(btn) {
    const parent = btn.parentNode;
    parent.querySelectorAll('.opt').forEach(opt => opt.classList.remove('is-on'));
    btn.classList.add('is-on');

    const variantId = btn.getAttribute('data-variant-id');
    const priceText = btn.getAttribute('data-variant-price');

    document.querySelector('#qv-selected-variant-id').value = variantId;
    document.querySelector('#qv-price-tag').textContent = priceText;
  };
};

// ── Accordions Handler ──────────────────────────────────────────────────────
ILAVYA.initAccordions = function() {
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.acc__btn');
    if (btn) {
      e.preventDefault();
      const item = btn.closest('.acc__item');
      if (item) {
        item.classList.toggle('is-open');
      }
    }
  });
};

// ── Product Gallery Switcher ────────────────────────────────────────────────
ILAVYA.initProductGallery = function() {
  /*
   * Product gallery navigation.
   *
   * IMPORTANT:
   * Navigation changes ONLY the gallery slide.
   * It does not scroll the page, focus the thumbnail, or change
   * the browser URL.
   */

  function getGallery(el) {
    return el ? el.closest('.gallery') : null;
  }

  function getSlides(gallery) {
    return gallery
      ? Array.from(gallery.querySelectorAll('.gallery__slide'))
      : [];
  }

  function getThumbs(gallery) {
    return gallery
      ? Array.from(gallery.querySelectorAll('.gallery__thumb'))
      : [];
  }

  function getCurrentIndex(gallery) {
    const stored = parseInt(
      gallery.getAttribute('data-gallery-index'),
      10
    );

    const slides = getSlides(gallery);

    if (
      !Number.isNaN(stored) &&
      stored >= 0 &&
      stored < slides.length
    ) {
      return stored;
    }

    const active = gallery.querySelector('.gallery__thumb.is-on');

    if (active) {
      const index = parseInt(
        active.getAttribute('data-thumb'),
        10
      );

      if (!Number.isNaN(index)) return index;
    }

    const visible = slides.findIndex(slide => {
      return window.getComputedStyle(slide).display !== 'none';
    });

    return visible >= 0 ? visible : 0;
  }

  function updateArrows(gallery, index) {
    const slides = getSlides(gallery);

    const prev = gallery.querySelector(
      '.gallery-nav--prev, [data-gallery-nav="prev"]'
    );

    const next = gallery.querySelector(
      '.gallery-nav--next, [data-gallery-nav="next"]'
    );

    if (prev) {
      const disabled = index <= 0;
      prev.disabled = disabled;
      prev.setAttribute(
        'aria-disabled',
        disabled ? 'true' : 'false'
      );
      prev.classList.toggle('is-disabled', disabled);
    }

    if (next) {
      const disabled = index >= slides.length - 1;
      next.disabled = disabled;
      next.setAttribute(
        'aria-disabled',
        disabled ? 'true' : 'false'
      );
      next.classList.toggle('is-disabled', disabled);
    }
  }

  function showSlide(gallery, requestedIndex) {
    if (!gallery) return;

    const slides = getSlides(gallery);
    const thumbs = getThumbs(gallery);

    if (!slides.length) return;

    let index = parseInt(requestedIndex, 10);

    if (Number.isNaN(index)) index = 0;

    index = Math.max(
      0,
      Math.min(index, slides.length - 1)
    );

    /*
     * Capture the exact page position before changing anything.
     * This prevents the gallery interaction from moving the viewport.
     */
    const savedScrollX = window.scrollX;
    const savedScrollY = window.scrollY;

    gallery.setAttribute(
      'data-gallery-index',
      String(index)
    );

    // Change ONLY slide visibility.
    slides.forEach((slide, slideIndex) => {
      const active = slideIndex === index;

      slide.style.display = active ? 'block' : 'none';
      slide.setAttribute(
        'aria-hidden',
        active ? 'false' : 'true'
      );
    });

    // Update active thumbnail without scrolling/focusing it.
    thumbs.forEach((thumb, thumbIndex) => {
      const dataIndex = parseInt(
        thumb.getAttribute('data-thumb'),
        10
      );

      const active = !Number.isNaN(dataIndex)
        ? dataIndex === index
        : thumbIndex === index;

      thumb.classList.toggle('is-on', active);

      if (active) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });

    updateArrows(gallery, index);

    /*
     * Restore the viewport after the browser has had a chance to
     * recalculate layout. This is what prevents the page from jumping.
     */
    window.scrollTo({
      left: savedScrollX,
      top: savedScrollY,
      behavior: 'instant'
    });

    requestAnimationFrame(() => {
      window.scrollTo({
        left: savedScrollX,
        top: savedScrollY,
        behavior: 'instant'
      });
    });
  }

  /*
   * Delegated handler:
   * works for normal page loads and Shopify Theme Editor re-renders.
   */
  if (!document.documentElement.dataset.ilavyaGalleryHandler) {

    document.documentElement.dataset.ilavyaGalleryHandler = 'true';

    document.addEventListener('click', function(event) {

      const nav = event.target.closest('.gallery-nav');

      if (nav) {
        event.preventDefault();
        event.stopPropagation();

        if (nav.disabled) return;

        const gallery = getGallery(nav);
        if (!gallery) return;

        const slides = getSlides(gallery);
        if (!slides.length) return;

        const current = getCurrentIndex(gallery);

        const direction =
          nav.classList.contains('gallery-nav--prev') ||
          nav.getAttribute('data-gallery-nav') === 'prev'
            ? -1
            : 1;

        const target = current + direction;

        if (
          target < 0 ||
          target >= slides.length
        ) {
          return;
        }

        showSlide(gallery, target);
        return;
      }

      const thumb = event.target.closest('.gallery__thumb');

      if (thumb) {
        event.preventDefault();

        const gallery = getGallery(thumb);
        if (!gallery) return;

        const index = parseInt(
          thumb.getAttribute('data-thumb'),
          10
        );

        if (Number.isNaN(index)) return;

        showSlide(gallery, index);
      }
    });

    document.addEventListener('keydown', function(event) {

      if (
        event.key !== 'ArrowLeft' &&
        event.key !== 'ArrowRight'
      ) {
        return;
      }

      const active = document.activeElement;

      if (
        active &&
        (
          active.tagName === 'INPUT' ||
          active.tagName === 'TEXTAREA' ||
          active.tagName === 'SELECT'
        )
      ) {
        return;
      }

      const gallery =
        active?.closest('.gallery') ||
        document.querySelector('.gallery');

      if (!gallery) return;

      const slides = getSlides(gallery);
      if (slides.length < 2) return;

      const current = getCurrentIndex(gallery);

      const direction =
        event.key === 'ArrowLeft' ? -1 : 1;

      const target = current + direction;

      if (
        target < 0 ||
        target >= slides.length
      ) {
        return;
      }

      event.preventDefault();
      showSlide(gallery, target);
    });
  }

  // Initialize existing galleries.
  document.querySelectorAll('.gallery').forEach(gallery => {
    const slides = getSlides(gallery);

    if (!slides.length) return;

    showSlide(
      gallery,
      getCurrentIndex(gallery)
    );
  });

  // Shopify Theme Editor section reload.
  if (!document.documentElement.dataset.ilavyaGallerySectionHandler) {

    document.documentElement.dataset.ilavyaGallerySectionHandler = 'true';

    document.addEventListener(
      'shopify:section:load',
      function(event) {

        const section = event.target;

        if (!section) return;

        section
          .querySelectorAll('.gallery')
          .forEach(gallery => {

            const slides = getSlides(gallery);

            if (!slides.length) return;

            showSlide(
              gallery,
              getCurrentIndex(gallery)
            );
          });
      }
    );
  }
};
ILAVYA.initProductVariants = function() {
  const buyBox = document.querySelector('.buybox');
  if (!buyBox) return;

  let currentRawPrice = 0;
  const initialSelectedOpt = buyBox.querySelector('.opt.variant-select-btn.is-on') || buyBox.querySelector('.opt[data-variant-id]');
  if (initialSelectedOpt) {
    currentRawPrice = parseFloat(initialSelectedOpt.getAttribute('data-price') || 0);
  }

  let isSubscribeSelected = false;

  function updatePrices() {
    if (!currentRawPrice) return;
    const oneTimeVal = currentRawPrice > 10000 ? Math.round(currentRawPrice / 100) : Math.round(currentRawPrice);
    const subVal = Math.round(oneTimeVal * 0.9);

    const oneTimeEl = buyBox.querySelector('#one-time-price-display');
    if (oneTimeEl) oneTimeEl.textContent = `₹${oneTimeVal.toLocaleString('en-IN')}`;

    const subEl = buyBox.querySelector('#subscribe-price-display');
    if (subEl) subEl.textContent = `₹${subVal.toLocaleString('en-IN')}`;

    const activePrice = isSubscribeSelected ? subVal : oneTimeVal;
    const buyboxPriceEl = buyBox.querySelector('#buybox-price-display') || buyBox.querySelector('.buybox__price strong');
    if (buyboxPriceEl) buyboxPriceEl.textContent = `₹${activePrice.toLocaleString('en-IN')}`;

    const stickyPrice = document.querySelector('.stickybuy #sticky-price-tag');
    if (stickyPrice) stickyPrice.textContent = `₹${activePrice.toLocaleString('en-IN')}`;
  }

  // Variant selector buttons
  const variantOpts = buyBox.querySelectorAll('.opt[data-variant-id]');
  variantOpts.forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      variantOpts.forEach(opt => opt.classList.remove('is-on'));
      btn.classList.add('is-on');

      const variantId = btn.getAttribute('data-variant-id');
      const rawPrice = parseFloat(btn.getAttribute('data-price') || 0);
      if (rawPrice) currentRawPrice = rawPrice;

      const compareText = btn.getAttribute('data-compare-price');

      const input = buyBox.querySelector('input[name="id"]');
      if (input) input.value = variantId;

      const stickyInput = document.querySelector('#sticky-selected-variant-id');
      if (stickyInput) stickyInput.value = variantId;

      /* Show the pack shot for the size just picked. The thumb carries the
         variant ids that use its photo, so clicking it reuses the gallery's
         own navigation instead of duplicating the slide logic. Products whose
         variants share one photo have no match, and the gallery stays put. */
      const packThumb = document.querySelector(
        '.gallery__thumb[data-variant-ids~="' + variantId + '"]'
      );
      if (packThumb && !packThumb.classList.contains('is-on')) packThumb.click();

      // Keep the pack block in sync with the selected variant (audit P0-09/P0-10)
      const packSizeEl = document.getElementById('pack-size-display');
      const vTitle = btn.getAttribute('data-variant-title');
      if (packSizeEl && vTitle) packSizeEl.textContent = vTitle;
      const netWeightEl = document.getElementById('net-weight-display');
      const netWeightSpec = document.getElementById('net-weight-spec');
      if (netWeightEl && netWeightSpec) {
        const vWeight = btn.getAttribute('data-variant-weight');
        if (vWeight) {
          netWeightEl.textContent = vWeight;
          netWeightSpec.style.display = '';
        } else {
          netWeightSpec.style.display = 'none';
        }
      }

      const compareEl = buyBox.querySelector('#buybox-compare-display') || buyBox.querySelector('.buybox__price s');
      if (compareEl) {
        if (compareText && compareText !== '0') {
          const compVal = parseFloat(compareText);
          const compRupees = compVal > 10000 ? Math.round(compVal / 100) : Math.round(compVal);
          compareEl.textContent = `₹${compRupees.toLocaleString('en-IN')}`;
          compareEl.style.display = 'inline';
        } else {
          compareEl.style.display = 'none';
        }
      }

      updatePrices();
    });
  });

  // How often / Purchase mode buttons
  const purchaseOpts = buyBox.querySelectorAll('.purchase-mode-btn');
  const deliveryPropInput = buyBox.querySelector('#delivery-prop-input');
  purchaseOpts.forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      purchaseOpts.forEach(opt => opt.classList.remove('is-on'));
      btn.classList.add('is-on');

      isSubscribeSelected = (btn.getAttribute('data-subscribe') === 'true');
      if (deliveryPropInput) {
        deliveryPropInput.disabled = !isSubscribeSelected;
      }
      updatePrices();
    });
  });

  // Quantity stepper (- 1 +)
  const qtyDec = buyBox.querySelector('#qty-dec');
  const qtyInc = buyBox.querySelector('#qty-inc');
  const qtyDisplay = buyBox.querySelector('#qty-display');
  const qtyInput = buyBox.querySelector('#quantity-input');
  let currentQty = 1;

  if (qtyDec && qtyInc && qtyDisplay && qtyInput) {
    qtyDec.addEventListener('click', (e) => {
      e.preventDefault();
      if (currentQty > 1) {
        currentQty--;
        qtyDisplay.textContent = currentQty;
        qtyInput.value = currentQty;
      }
    });

    qtyInc.addEventListener('click', (e) => {
      e.preventDefault();
      currentQty++;
      qtyDisplay.textContent = currentQty;
      qtyInput.value = currentQty;
    });
  }

  const stickyBar = document.querySelector('.stickybuy');
  if (stickyBar) {
    function toggleStickyBar() {
      const rect = buyBox.getBoundingClientRect();
      if (rect.bottom < 100) {
        stickyBar.style.display = 'flex';
      } else {
        stickyBar.style.display = 'none';
      }
    }

    window.addEventListener('scroll', toggleStickyBar, { passive: true });
    toggleStickyBar();
  }

  const stickyForm = document.querySelector('#sticky-buy-form');
  if (stickyForm) {
    stickyForm.addEventListener('submit', (e) => {
      e.preventDefault();
      /* This form's action matches the document-level cart delegate, so without
         stopping the bubble both handlers ran: two POSTs to cart/add.js and two
         toasts from a single click. Keep this handler — it copies the main
         form's data, so quantity and delivery properties survive, which the
         sticky form's lone id input would lose. */
      e.stopPropagation();
      const mainForm = document.querySelector('form[action*="/cart/add"]');
      const selectedIdInput = document.querySelector('#sticky-selected-variant-id');
      const selectedId = selectedIdInput ? selectedIdInput.value : null;

      if (mainForm && selectedId) {
        const formData = new FormData(mainForm);
        formData.set('id', selectedId);
        
        fetch((window.Shopify.routes.root || '/') + 'cart/add.js', {
          method: 'POST',
          body: formData
        })
        .then(res => res.json())
        .then(() => {
          ILAVYA.refreshCartDrawer();
          ILAVYA.openOverlay('cart');
          ILAVYA.toast('Added to bag');
        })
        .catch(err => console.error('Error adding sticky item to cart:', err));
      }
    });
  }

  const stickyScrollBtn = document.querySelector('.stickybuy-scroll-trigger');
  if (stickyScrollBtn) {
    stickyScrollBtn.addEventListener('click', (e) => {
      e.preventDefault();
      buyBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }
};

// ── Ritual Finder Quiz (Vanilla JS Implementation) ──────────────────────────
ILAVYA.initRitualFinder = function() {
  const quizSection = document.querySelector('.quiz-section-wrapper');
  if (!quizSection) return;

  const quizData = [
    {
      id: 'tod',
      question: 'When is your day hardest to handle?',
      help: 'We map this to caffeine allowance and adaptogen timings.',
      options: [
        { id: 'morning', label: 'First thing in the morning', sub: 'Sluggish start, needing focus' },
        { id: 'afternoon', label: 'The 3pm slump', sub: 'Needing clean energy to finish the day' },
        { id: 'evening', label: 'Late evening / night', sub: 'Needing to quieten the head and wind down' }
      ]
    },
    {
      id: 'outcome',
      question: 'What do you want to feel instead?',
      help: 'Each option maps to specific bio-active botanicals.',
      options: [
        { id: 'energy', label: 'Sustained energy', sub: 'Steady drive without jitters or crashes' },
        { id: 'calm', label: 'Mental calm', sub: 'Handling a busy head and stress load' },
        { id: 'digestion', label: 'Lighter digestion', sub: 'Beating bloating and heavy stomach' },
        { id: 'immunity', label: 'Higher resilience', sub: 'Daily protection and antioxidant support' }
      ]
    },
    {
      id: 'temp',
      question: 'How do you prefer to drink your wellness?',
      help: 'Warm cups trigger slower moments; cold coolers fit fast ones.',
      options: [
        { id: 'hot', label: 'Warming & hot', sub: 'Slow sips, hot water or milk' },
        { id: 'cold', label: 'Chilled & over ice', sub: 'Refreshing gulps, cold water' },
        { id: 'both', label: 'I like both depending on the season', sub: 'Versatile blends that work either way' }
      ]
    },
    {
      id: 'flavor',
      question: 'What taste family do you reach for?',
      help: 'Your palate determines what habit survives a bad week.',
      options: [
        { id: 'chocolate', label: 'Rich, chocolate & spiced', sub: 'Comforting, dark cacao, chai spice' },
        { id: 'fruit', label: 'Light, fruity & floral', sub: 'Tart hibiscus, peach, citrus highlights' },
        { id: 'green', label: 'Clean, green & earthy', sub: 'Moringa, grassy herbs, slight lemon' }
      ]
    },
    {
      id: 'caffeine',
      question: 'Where do you stand on caffeine?',
      help: 'All ILAVYA blends are under 25mg; most are fully caffeine-free.',
      options: [
        { id: 'none', label: 'Zero caffeine only', sub: 'I am highly sensitive or drink it late' },
        { id: 'light', label: 'A light lift is fine', sub: 'Green tea levels are acceptable' }
      ]
    }
  ];

  let currentStep = 0;
  const answers = {};
  
  const progressFill = quizSection.querySelector('.quiz__progress-fill');
  const stepLabel = quizSection.querySelector('.quiz-step-label');
  const questionEl = quizSection.querySelector('.quiz__q');
  const helpEl = quizSection.querySelector('.quiz-help-text');
  const optsContainer = quizSection.querySelector('.quiz__opts');
  const backBtn = quizSection.querySelector('.quiz-back-btn');
  const introContainer = quizSection.querySelector('.quiz-intro');
  const quizActiveContainer = quizSection.querySelector('.quiz-active');
  const startBtn = quizSection.querySelector('.quiz-start-btn');
  const resultsContainer = quizSection.querySelector('.quiz-results');

  if (startBtn) {
    startBtn.addEventListener('click', () => {
      introContainer.style.display = 'none';
      quizActiveContainer.style.display = 'block';
      renderStep();
    });
  }

  if (backBtn) {
    backBtn.addEventListener('click', () => {
      if (currentStep > 0) {
        currentStep--;
        renderStep();
      }
    });
  }

  function renderStep() {
    const data = quizData[currentStep];
    
    progressFill.style.width = `${(currentStep / quizData.length) * 100}%`;
    stepLabel.textContent = `Question ${currentStep + 1} of ${quizData.length}`;
    
    backBtn.style.display = currentStep > 0 ? 'inline-flex' : 'none';
    
    questionEl.textContent = data.question;
    helpEl.textContent = data.help;
    
    optsContainer.innerHTML = '';
    data.options.forEach((opt, idx) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `quizopt ${answers[data.id] === opt.id ? 'is-on' : ''}`;
      btn.innerHTML = `
        <span>
          <span class="quizopt__label" style="display: block;">${opt.label}</span>
          <span class="quizopt__sub" style="display: block;">${opt.sub}</span>
        </span>
        <span class="quizopt__key" aria-hidden="true">${idx + 1}</span>
      `;
      btn.addEventListener('click', () => {
        answers[data.id] = opt.id;
        btn.classList.add('is-on');
        
        setTimeout(() => {
          if (currentStep < quizData.length - 1) {
            currentStep++;
            renderStep();
          } else {
            showQuizResults();
          }
        }, 220);
      });
      optsContainer.appendChild(btn);
    });
  }

  function showQuizResults() {
    quizActiveContainer.style.display = 'none';
    resultsContainer.style.display = 'block';
    
    const scriptEl = document.querySelector('#quiz-products-json');
    if (!scriptEl) return;
    
    try {
      const allProducts = JSON.parse(scriptEl.textContent);
      
      const scored = allProducts.map(p => {
        let score = 0;
        
        if (p.goals && p.goals.includes(answers.outcome)) score += 10;
        if (p.timeOfDay && p.timeOfDay.includes(answers.tod)) score += 8;
        if (p.serveTemp === answers.temp || p.serveTemp === 'both') score += 5;
        if (p.flavor === answers.flavor) score += 4;
        
        if (p.caffeine === answers.caffeine) {
          score += 6;
        } else if (answers.caffeine === 'none' && p.caffeine === 'light') {
          score -= 15;
        }
        
        return { product: p, score };
      }).sort((a, b) => b.score - a.score);

      const primary = scored[0].product;
      const morning = scored.find(s => s.product.timeOfDay && s.product.timeOfDay.includes('morning'))?.product || primary;
      const evening = scored.find(s => s.product.timeOfDay && s.product.timeOfDay.includes('evening') && s.product.handle !== morning.handle)?.product || primary;

      const primaryTitle = resultsContainer.querySelector('#result-primary-title');
      const primaryDesc = resultsContainer.querySelector('#result-primary-desc');
      const primaryStory = resultsContainer.querySelector('#result-primary-story');
      const primaryImage = resultsContainer.querySelector('#result-primary-image img');
      const primaryAddBtn = resultsContainer.querySelector('#result-primary-add-btn');
      const primaryLink = resultsContainer.querySelector('#result-primary-link');
      
      if (primaryTitle) primaryTitle.textContent = primary.title;
      if (primaryDesc) primaryDesc.textContent = primary.description.substring(0, 160) + '…';
      if (primaryStory) primaryStory.textContent = primary.story || 'A perfect fit for your daily habit.';
      if (primaryImage) {
        primaryImage.src = primary.image || 'data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 4 5\'%3E%3C/svg%3E';
        primaryImage.alt = primary.title;
      }
      
      if (primaryAddBtn) {
        primaryAddBtn.textContent = `Add discovery pack · ₹${(primary.price/100).toFixed(0)}`;
        primaryAddBtn.onclick = () => ILAVYA.quickAdd(primary.variantId);
      }
      if (primaryLink) primaryLink.href = primary.url;

      const morningVisual = resultsContainer.querySelector('#routine-morning-visual img');
      const morningTitle = resultsContainer.querySelector('#routine-morning-title');
      const morningDesc = resultsContainer.querySelector('#routine-morning-desc');
      const eveningVisual = resultsContainer.querySelector('#routine-evening-visual img');
      const eveningTitle = resultsContainer.querySelector('#routine-evening-title');
      const eveningDesc = resultsContainer.querySelector('#routine-evening-desc');

      if (morningTitle && morning) {
        morningTitle.textContent = morning.title;
        if (morningDesc) morningDesc.textContent = morning.description.substring(0, 80) + '…';
        if (morningVisual) {
          morningVisual.src = morning.image;
          morningVisual.alt = morning.title;
        }
      }
      
      if (eveningTitle && evening) {
        eveningTitle.textContent = evening.title;
        if (eveningDesc) eveningDesc.textContent = evening.description.substring(0, 80) + '…';
        if (eveningVisual) {
          eveningVisual.src = evening.image;
          eveningVisual.alt = evening.title;
        }
      }

      const routineAddBtn = resultsContainer.querySelector('#routine-add-all-btn');
      if (routineAddBtn) {
        routineAddBtn.onclick = () => {
          const items = [];
          if (morning) items.push({ id: morning.variantId, quantity: 1 });
          if (evening && evening.handle !== morning.handle) items.push({ id: evening.variantId, quantity: 1 });
          
          fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items })
          })
          .then(res => res.json())
          .then(() => {
            ILAVYA.refreshCartDrawer();
            ILAVYA.openOverlay('cart');
            ILAVYA.toast('Morning and Evening blends added!');
          });
        };
      }

      const restartBtn = resultsContainer.querySelector('#routine-restart-btn');
      if (restartBtn) {
        restartBtn.onclick = () => {
          currentStep = 0;
          Object.keys(answers).forEach(k => delete answers[k]);
          resultsContainer.style.display = 'none';
          quizActiveContainer.style.display = 'block';
          renderStep();
        };
      }

    } catch (e) {
      console.error('Quiz parsing error:', e);
    }
  }
};

// ── Ingredient Index Search & Filters ──────────────────────────────────────
ILAVYA.initIngredientIndex = function() {
  const container = document.querySelector('.ingredients-index-wrapper');
  if (!container) return;

  const cards = container.querySelectorAll('.incard');
  const groupChips = container.querySelectorAll('.chip[data-group]');
  const searchInput = container.querySelector('.ingredients-search-input');
  
  let currentGroup = 'all';
  let currentQuery = '';

  function filterList() {
    const q = currentQuery.trim().toLowerCase();
    cards.forEach(card => {
      const group = card.getAttribute('data-group');
      const text = card.textContent.toLowerCase();
      
      const matchesGroup = (
        currentGroup === 'all' ||
        group === currentGroup ||
        (currentGroup === 'botanical' && group === 'botanicals') ||
        (currentGroup === 'sweetener' && group === 'sweeteners') ||
        (currentGroup === 'base' && (group === 'minerals' || group === 'additives'))
      );
      const matchesSearch = (!q || text.includes(q));

      if (matchesGroup && matchesSearch) {
        card.style.display = 'flex';
      } else {
        card.style.display = 'none';
      }
    });
  }

  groupChips.forEach(chip => {
    chip.addEventListener('click', (e) => {
      e.preventDefault();
      groupChips.forEach(c => c.classList.remove('is-on'));
      chip.classList.add('is-on');
      
      currentGroup = chip.getAttribute('data-group');
      filterList();
    });
  });

  if (searchInput) {
    searchInput.addEventListener('input', () => {
      currentQuery = searchInput.value;
      filterList();
    });
  }

  const detailsPanel = container.querySelector('#ingredient-detail');
  const detailsClose = container.querySelector('#ingredient-detail-close');
  
  ILAVYA.openIngredientDetail = function(slug) {
    if (!detailsPanel) return;

    const dataScript = container.querySelector(`#ingredient-data-${slug}`);
    if (!dataScript) return;

    try {
      const data = JSON.parse(dataScript.textContent);
      
      detailsPanel.querySelector('.indetail h2').textContent = data.name;
      detailsPanel.querySelector('.indetail .lead').textContent = data.short;
      detailsPanel.querySelector('.indetail .body-lg').textContent = data.body;
      detailsPanel.querySelector('.ingredient-does-desc').textContent = data.does;
      detailsPanel.querySelector('.ingredient-origin-desc').textContent = data.origin;
      
      const tagsContainer = detailsPanel.querySelector('.ingredient-facts-tags');
      tagsContainer.innerHTML = '';
      if (data.keyFacts) {
        data.keyFacts.forEach(fact => {
          tagsContainer.innerHTML += `<span class="tag">${fact}</span>`;
        });
      }

      const photoEl = detailsPanel.querySelector('.ingredient-sourced-photo');
      if (photoEl && data.image) {
        photoEl.src = data.image;
        photoEl.alt = data.name;
        photoEl.style.display = 'block';
      } else if (photoEl) {
        photoEl.style.display = 'none';
      }

      const cautionEl = detailsPanel.querySelector('.ingredient-caution-notice');
      if (cautionEl) {
        if (data.caution) {
          cautionEl.innerHTML = `<strong>Note:</strong> ${data.caution}`;
          cautionEl.style.display = 'block';
        } else {
          cautionEl.style.display = 'none';
        }
      }

      const blendsGrid = detailsPanel.querySelector('.ingredient-blends-grid');
      const blendsHeader = detailsPanel.querySelector('.ingredient-blends-header');
      blendsGrid.innerHTML = '';
      
      if (data.blends && data.blends.length > 0) {
        blendsHeader.style.display = 'flex';
        data.blends.forEach(b => {
          blendsGrid.innerHTML += `
            <article class="pcard" style="--accent: #b4542b; --accent-soft: #f6e3d8;">
              <div class="pcard__media">
                <a href="${b.url}">
                  <div class="pack">
                    <img src="${b.image}" alt="${b.title}" class="pack__img">
                  </div>
                </a>
              </div>
              <div class="pcard__body">
                <h3 class="pcard__name"><a href="${b.url}" class="link">${b.title}</a></h3>
              </div>
            </article>
          `;
        });
      } else {
        blendsHeader.style.display = 'none';
      }

      detailsPanel.style.display = 'block';
      detailsPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });

    } catch (e) {
      console.error('Error opening ingredient detail:', e);
    }
  };

  ILAVYA.closeIngredientDetail = function() {
    if (detailsPanel) detailsPanel.style.display = 'none';
  };

  if (detailsClose) {
    detailsClose.addEventListener('click', () => {
      ILAVYA.closeIngredientDetail();
    });
  }

  const params = new URLSearchParams(window.location.search);
  const openSlug = params.get('open');
  if (openSlug) {
    setTimeout(() => {
      ILAVYA.openIngredientDetail(openSlug);
    }, 300);
  }
};

// ── Global Scroll Reveal (.reveal -> .reveal.is-in) ───────────────────────
ILAVYA.initScrollReveal = function() {
  function revealOnScroll() {
    const reveals = document.querySelectorAll('.reveal:not(.is-in)');
    if (reveals.length === 0) return;
    
    const windowHeight = window.innerHeight;
    reveals.forEach(el => {
      const rect = el.getBoundingClientRect();
      if (rect.top < windowHeight * 0.92 && rect.bottom > 0) {
        el.classList.add('is-in');
      }
    });
  }

  window.addEventListener('scroll', revealOnScroll, { capture: true, passive: true });
  window.addEventListener('resize', revealOnScroll, { passive: true });
  window.addEventListener('load', revealOnScroll);

  revealOnScroll();
  setTimeout(revealOnScroll, 100);
  setTimeout(revealOnScroll, 300);
  setTimeout(revealOnScroll, 800);
};

// ── Mobile Collection Filters Drawer Toggle ─────────────────────────────────
ILAVYA.initFilters = function() {
  const filtersModal = document.getElementById('collection-filters');
  const openBtn = document.getElementById('open-filters-btn');
  const closeBtnX = document.getElementById('close-filters-x');
  const closeBtnApply = document.getElementById('close-filters-btn');

  if (!filtersModal) return;

  function openFilters() {
    filtersModal.classList.remove('is-hidden');
    document.body.style.overflow = 'hidden';
  }

  function closeFilters() {
    filtersModal.classList.add('is-hidden');
    document.body.style.overflow = '';
  }

  if (openBtn) {
    openBtn.addEventListener('click', openFilters);
  }
  if (closeBtnX) {
    closeBtnX.addEventListener('click', closeFilters);
  }
  if (closeBtnApply) {
    closeBtnApply.addEventListener('click', closeFilters);
  }

  window.addEventListener('resize', () => {
    if (window.innerWidth > 1024) {
      document.body.style.overflow = '';
    }
  }, { passive: true });
};

// ── Review Modal & Photo/Video Upload Handler ────────────────────────────────
ILAVYA.initReviewModal = function() {
  const openBtn = document.querySelector('#open-write-review-btn');
  const scrim = document.querySelector('#review-modal-scrim');
  const panel = document.querySelector('#review-modal-panel');
  const closeBtn = document.querySelector('#close-review-modal-btn');
  const cancelBtn = document.querySelector('#cancel-review-modal-btn');
  const form = document.querySelector('#write-review-form');
  const starSelect = document.querySelector('#star-rating-select');
  const ratingInput = document.querySelector('#review-form-rating');
  const fileInput = document.querySelector('#review-file-input');
  const previewDiv = document.querySelector('#review-media-preview');
  const reviewsList = document.querySelector('#reviews-list');

  if (!openBtn || !panel) return;

  function openModal() {
    if (scrim) scrim.style.display = 'block';
    panel.style.display = 'block';
    document.body.classList.add('is-locked');
  }

  function closeModal() {
    if (scrim) scrim.style.display = 'none';
    panel.style.display = 'none';
    document.body.classList.remove('is-locked');
  }

  openBtn.addEventListener('click', openModal);
  if (closeBtn) closeBtn.addEventListener('click', closeModal);
  if (cancelBtn) cancelBtn.addEventListener('click', closeModal);
  if (scrim) scrim.addEventListener('click', closeModal);

  // Star Rating Selection
  if (starSelect && ratingInput) {
    const starBtns = starSelect.querySelectorAll('.star-select-btn');
    starBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const val = parseInt(btn.getAttribute('data-value'), 10);
        ratingInput.value = val;
        starBtns.forEach((s, idx) => {
          if (idx < val) {
            s.style.color = 'var(--clay, #b4542b)';
          } else {
            s.style.color = 'var(--line-3, #dcd7ce)';
          }
        });
      });
    });
  }

  // File Upload Previews (Photo & Video)
  let uploadedFiles = [];
  if (fileInput && previewDiv) {
    fileInput.addEventListener('change', () => {
      previewDiv.innerHTML = '';
      uploadedFiles = Array.from(fileInput.files);

      uploadedFiles.forEach((file, index) => {
        const itemWrap = document.createElement('div');
        itemWrap.style.cssText = 'width:72px; height:72px; border-radius:var(--r-md); overflow:hidden; position:relative; background:#000; border:1px solid var(--line);';

        const removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.innerHTML = '&times;';
        removeBtn.style.cssText = 'position:absolute; top:2px; right:2px; width:18px; height:18px; border-radius:50%; background:rgba(0,0,0,0.7); color:#fff; border:none; font-size:12px; cursor:pointer; line-height:1; display:flex; align-items:center; justify-content:center; z-index:2;';
        removeBtn.onclick = (e) => {
          e.preventDefault();
          uploadedFiles.splice(index, 1);
          itemWrap.remove();
        };

        const fileUrl = URL.createObjectURL(file);
        if (file.type.startsWith('video/')) {
          const video = document.createElement('video');
          video.src = fileUrl;
          video.style.cssText = 'width:100%; height:100%; object-fit:cover;';
          itemWrap.appendChild(video);
        } else {
          const img = document.createElement('img');
          img.src = fileUrl;
          img.style.cssText = 'width:100%; height:100%; object-fit:cover;';
          itemWrap.appendChild(img);
        }
        itemWrap.appendChild(removeBtn);
        previewDiv.appendChild(itemWrap);
      });
    });
  }

  // Form Submission
  if (form) {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const formData = new FormData(form);

      fetch(form.getAttribute('action') || '/contact', {
        method: 'POST',
        body: formData
      })
      .then(() => {
        closeModal();
        form.reset();
        if (previewDiv) previewDiv.innerHTML = '';
        uploadedFiles = [];
        if (typeof ILAVYA.toast === 'function') {
          ILAVYA.toast('Thank you! Your review has been submitted for moderation and will appear once approved by admin.');
        }
      })
      .catch(err => {
        console.error('Review submission error:', err);
        closeModal();
        form.reset();
        if (previewDiv) previewDiv.innerHTML = '';
        uploadedFiles = [];
        if (typeof ILAVYA.toast === 'function') {
          ILAVYA.toast('Thank you! Your review has been submitted for moderation and will appear once approved by admin.');
        }
      });
    });
  }
};

// ── Real-Time Instant AJAX Collection Filters ───────────────────────────────
ILAVYA.initFilters = function() {
  const shopEl = document.querySelector('.shop') || document.querySelector('.shop-collection-tabs');
  if (!shopEl) return;

  // Mobile Drawer toggles
  const openBtn = document.querySelector('#open-filters-btn');
  const closeBtn = document.querySelector('#close-filters-btn');
  const closeX = document.querySelector('#close-filters-x');
  const filtersAside = document.querySelector('#collection-filters');

  function openDrawer() {
    if (filtersAside) {
      filtersAside.classList.remove('is-hidden');
      document.body.style.overflow = 'hidden';
    }
  }

  function closeDrawer() {
    if (filtersAside) {
      filtersAside.classList.add('is-hidden');
      document.body.style.overflow = '';
    }
  }

  if (openBtn) openBtn.addEventListener('click', openDrawer);
  if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
  if (closeX) closeX.addEventListener('click', closeDrawer);

  let isFetching = false;

  function fetchAndApply(url, pushState = true) {
    if (isFetching) return;
    isFetching = true;

    const grid = document.querySelector('.pgrid');
    if (grid) grid.style.opacity = '0.35';

    fetch(url)
      .then(res => {
        if (!res.ok) throw new Error('Network error');
        return res.text();
      })
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // 1. Update Title & Header Description
        const newHead = doc.querySelector('.phead');
        const oldHead = document.querySelector('.phead');
        if (newHead && oldHead) oldHead.innerHTML = newHead.innerHTML;

        // 2. Update Top Horizontal Collection Tabs
        const newTabs = doc.querySelector('.shop-collection-tabs');
        const oldTabs = document.querySelector('.shop-collection-tabs');
        if (newTabs && oldTabs) oldTabs.innerHTML = newTabs.innerHTML;

        // 3. Update Sidebar Filters
        const newFilters = doc.querySelector('#collection-filters');
        const oldFilters = document.querySelector('#collection-filters');
        if (newFilters && oldFilters) oldFilters.innerHTML = newFilters.innerHTML;

        // 4. Update Active Filters Pills
        const newActive = doc.querySelector('.shop__active');
        const oldActive = document.querySelector('.shop__active');
        if (newActive && oldActive) {
          oldActive.innerHTML = newActive.innerHTML;
        } else if (newActive && !oldActive) {
          const shopBar = document.querySelector('.shop__bar');
          if (shopBar) shopBar.insertAdjacentElement('afterend', newActive);
        } else if (!newActive && oldActive) {
          oldActive.remove();
        }

        // 5. Update Product Grid
        const newGrid = doc.querySelector('.pgrid');
        const oldGrid = document.querySelector('.pgrid');
        if (newGrid && oldGrid) {
          oldGrid.innerHTML = newGrid.innerHTML;
          oldGrid.style.opacity = '1';

          // Make all dynamically inserted product cards immediately visible
          oldGrid.querySelectorAll('.reveal').forEach(el => {
            el.classList.add('is-in');
            el.style.opacity = '1';
            el.style.transform = 'none';
          });
        }

        // 6. Update Product Count
        const newCount = doc.querySelector('.shop__bar .small.muted');
        const oldCount = document.querySelector('.shop__bar .small.muted');
        if (newCount && oldCount) oldCount.innerHTML = newCount.innerHTML;

        // 7. Re-bind Mobile Drawer Buttons if updated
        const newOpenBtn = document.querySelector('#open-filters-btn');
        const newCloseBtn = document.querySelector('#close-filters-btn');
        const newCloseX = document.querySelector('#close-filters-x');
        const newFiltersAside = document.querySelector('#collection-filters');
        if (newOpenBtn) newOpenBtn.onclick = openDrawer;
        if (newCloseBtn) newCloseBtn.onclick = closeDrawer;
        if (newCloseX) newCloseX.onclick = closeDrawer;

        // 8. Update URL in Browser History
        if (pushState && window.history.pushState) {
          window.history.pushState({}, '', url);
        }

        isFetching = false;
      })
      .catch(err => {
        console.error('Realtime filter fetch error:', err);
        isFetching = false;
        if (grid) grid.style.opacity = '1';
        window.location.href = url;
      });
  }

  // Intercept Filter Clicks (top tabs, sidebar chips, active filter tags, clear links)
  document.addEventListener('click', function(e) {
    const link = e.target.closest('.shop-collection-tabs a, #collection-filters a, .shop__active a');
    if (!link) return;

    const href = link.getAttribute('href');
    if (href && !href.startsWith('#') && !href.startsWith('javascript:')) {
      e.preventDefault();
      fetchAndApply(href, true);
    }
  });

  // Intercept Input Changes & Native OS2 Form Submits
  document.addEventListener('change', function(e) {
    const el = e.target.closest('#collection-filters select, #collection-filters form input');
    if (!el) return;

    if (el.tagName === 'SELECT') {
      const val = el.value;
      if (val) {
        e.preventDefault();
        fetchAndApply(val, true);
      }
    } else if (el.form) {
      e.preventDefault();
      const form = el.form;
      const formData = new FormData(form);
      const searchParams = new URLSearchParams(formData).toString();
      const action = form.getAttribute('action') || window.location.pathname;
      const targetUrl = action + (searchParams ? '?' + searchParams : '');
      fetchAndApply(targetUrl, true);
    }
  });

  // Browser Back / Forward Button Handling
  window.addEventListener('popstate', function() {
    if (document.querySelector('.shop')) {
      fetchAndApply(window.location.href, false);
    }
  });
};

/* ══════════════════════════════════════════════════════════════════════════
   PREMIUM REDESIGN — Aug 2026
   Restrained motion utilities used by the editorial sections. Everything
   here is opt-in via a data attribute and silent when the user has asked
   for reduced motion.
   ══════════════════════════════════════════════════════════════════════════ */

(function () {
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ── Subtle parallax ────────────────────────────────────────────────────
     Only ever moves the image inside its own frame, never more than a few
     percent, and only while the frame is on screen. */
  function initParallax() {
    if (reduce.matches) return;

    var frames = [].slice.call(document.querySelectorAll('[data-parallax]:not([data-parallax-init])'));
    if (!frames.length) return;
    frames.forEach(function (f) { f.setAttribute('data-parallax-init', '1'); });

    var live = [];
    var ticking = false;

    function place() {
      var vh = window.innerHeight || document.documentElement.clientHeight;
      live.forEach(function (frame) {
        var img = frame.querySelector('[data-parallax-img]');
        if (!img) return;
        var r = frame.getBoundingClientRect();
        // -1 when the frame is just below the fold, +1 when just above it.
        var ratio = (r.top + r.height / 2 - vh / 2) / (vh / 2 + r.height / 2);
        ratio = Math.max(-1, Math.min(1, ratio));
        img.style.transform = 'translate3d(0,' + (ratio * -6).toFixed(2) + '%,0)';
      });
      ticking = false;
    }

    function onScroll() {
      if (ticking || !live.length) return;
      ticking = true;
      requestAnimationFrame(place);
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          var i = live.indexOf(en.target);
          if (en.isIntersecting && i === -1) live.push(en.target);
          if (!en.isIntersecting && i > -1) live.splice(i, 1);
        });
        onScroll();
      }, { rootMargin: '15% 0px' });
      frames.forEach(function (f) { io.observe(f); });
    } else {
      live = frames;
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    place();
  }

  /* ── Count-up for editorial statistics ──────────────────────────────────
     Animates only the leading integer of a value like "11", "100%" or
     "10 sec", so units and symbols survive untouched. */
  function initCountUp() {
    var nodes = [].slice.call(document.querySelectorAll('[data-countup]:not([data-countup-init])'));
    if (!nodes.length || !('IntersectionObserver' in window)) return;
    nodes.forEach(function (n) { n.setAttribute('data-countup-init', '1'); });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        io.unobserve(en.target);

        var el = en.target;
        var raw = el.textContent.trim();
        var m = raw.match(/^(\d+)(.*)$/);
        if (!m || reduce.matches) return;

        var target = parseInt(m[1], 10);
        var suffix = m[2];
        if (!target || target > 100000) return;

        var start = performance.now();
        var dur = 900;

        (function step(now) {
          var t = Math.min(1, (now - start) / dur);
          // easeOutExpo — fast, then settles
          var eased = t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
          el.textContent = Math.round(target * eased) + suffix;
          if (t < 1) requestAnimationFrame(step);
        })(start);
      });
    }, { threshold: 0.6 });

    nodes.forEach(function (n) { io.observe(n); });
  }

  function boot() {
    initParallax();
    initCountUp();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Section re-renders inside the theme editor need the same treatment.
  document.addEventListener('shopify:section:load', boot);
})();
